import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { AccessibilityAuditToolNames } from '../../../shared/constants/audit-tool';
import {
  IPageSummary,
  IPageSummaryServerResponse,
  IRuleSummary,
  ISummaryMetrics,
  ISummaryRequestQuery,
  IUserFlowScanPointSummary,
  IUserFlowScanPointSummaryServerResponse,
} from '../../../shared/interfaces/scan-summaries.interface';
import { ICurrentSelectedProperty } from './user.service';
import { RestService } from './rest.service';
import { UserPropertyService } from './user-property.service';
import { $ruleSummary, $scanSummaries } from '../../../shared/constants/scan-summaries';
import { IAuditRule } from '../../../shared/interfaces/audit-rule.interface';
import { SharedAuditsUtility } from '../../../shared/utils/audits.utility';
import { $auditRule } from '../../../shared/constants/audit-rule';
import { $severity } from '../../../shared/constants/accessibility';
import { IAudit } from '../../../shared/interfaces/audit.interface';
import { SharedCommonUtility } from '../../../shared/utils/common.utility';

@Injectable({
  providedIn: 'root',
})
export class PageSummariesService {
  constructor(
    private restService: RestService,
    private userPropertyService: UserPropertyService,
  ) {}

  public getUserFlowScanPointSummaryForTool(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    documentId: string,
    userFlowScanPointId: string,
    auditTool: AccessibilityAuditToolNames = AccessibilityAuditToolNames.ibm,
  ): Observable<IUserFlowScanPointSummary> {
    return this.restService.getUserFlowScanPointSummaryForTool(
      workspaceId,
      digitalPropertyId,
      scanId,
      documentId,
      userFlowScanPointId,
      auditTool,
    );
  }

  public getPageSummaryForTool(
    scanId: string,
    documentId: string,
    auditTool: AccessibilityAuditToolNames,
  ): Observable<IPageSummary> {
    return this.userPropertyService
      .currentSelectedProperty()
      .pipe(
        mergeMap(
          ({ workspaceId, digitalPropertyId }: ICurrentSelectedProperty): Observable<IPageSummary> =>
            this.restService.getPageSummaryForTool(workspaceId, digitalPropertyId, scanId, documentId, auditTool),
        ),
      );
  }

  public getPageSummariesForTool(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    params: ISummaryRequestQuery,
  ): Observable<IPageSummaryServerResponse> {
    return this.restService.getPageSummariesForTool(workspaceId, digitalPropertyId, scanId, params);
  }

  public getPageSummariesForUserFlowScanPoints(
    workspaceId: string,
    digitalPropertyId: string,
    scanId: string,
    params: ISummaryRequestQuery,
  ): Observable<IUserFlowScanPointSummaryServerResponse> {
    return this.restService.getPageSummariesForUserFlowScanPoints(workspaceId, digitalPropertyId, scanId, params);
  }

  public getOpenIssues(page: IPageSummary, audit: IAudit, componentId?: string): number {
    const metrics: ISummaryMetrics = page[$scanSummaries.metrics];
    let total: number = 0;

    for (const ruleSummary of Object.values(metrics)) {
      const ruleName: string = ruleSummary[$ruleSummary.ruleName];
      const ruleInfo: IAuditRule = audit.rules[ruleName];

      if (ruleSummary[$ruleSummary.triggered] && SharedAuditsUtility.hasWcag(ruleInfo[$auditRule.standards])) {
        total += this.getMetricFromRuleSummary(ruleSummary, $ruleSummary.errors, componentId);
      }
    }

    return total;
  }

  public getMetricFromRuleSummary(
    ruleSummary: IRuleSummary,
    metricName: $ruleSummary.errors | $ruleSummary.ignored | $ruleSummary.passed,
    componentId?: string,
  ): number {
    if (SharedCommonUtility.isNullish(componentId)) {
      return ruleSummary[metricName];
    }

    const metric: number = ruleSummary[$ruleSummary.components]?.[componentId][metricName];
    return Number.isInteger(metric) ? metric : 0;
  }

  public getDismissedIssues(page: IPageSummary, audit: IAudit, componentId?: string): number {
    const metrics: ISummaryMetrics = page[$scanSummaries.metrics];
    let total: number = 0;

    for (const ruleSummary of Object.values(metrics)) {
      const ruleName: string = ruleSummary[$ruleSummary.ruleName];
      const ruleInfo: IAuditRule = audit.rules[ruleName];

      if (ruleSummary[$ruleSummary.triggered] && SharedAuditsUtility.hasWcag(ruleInfo[$auditRule.standards])) {
        total += this.getMetricFromRuleSummary(ruleSummary, $ruleSummary.ignored, componentId);
      }
    }

    return total;
  }

  public getTotalFailedIssues(page: IPageSummary, audit: IAudit, componentId?: string): number {
    const metrics: ISummaryMetrics = page[$scanSummaries.metrics];
    let total: number = 0;

    for (const ruleSummary of Object.values(metrics)) {
      const ruleName: string = ruleSummary[$ruleSummary.ruleName];
      const ruleInfo: IAuditRule = audit.rules[ruleName];
      const severity: $severity = ruleInfo[$auditRule.severity];

      if (
        ruleSummary[$ruleSummary.triggered] &&
        SharedAuditsUtility.hasWcag(ruleInfo[$auditRule.standards]) &&
        severity !== $severity.info
      ) {
        total += this.getMetricFromRuleSummary(ruleSummary, $ruleSummary.errors, componentId);
      }
    }

    return total;
  }

  public getTotalReviewIssues(page: IPageSummary, audit: IAudit, componentId?: string): number {
    const metrics: ISummaryMetrics = page[$scanSummaries.metrics];
    let total: number = 0;

    for (const ruleSummary of Object.values(metrics)) {
      const ruleName: string = ruleSummary[$ruleSummary.ruleName];
      const ruleInfo: IAuditRule = audit.rules[ruleName];
      const severity: $severity = ruleInfo[$auditRule.severity];

      if (
        ruleSummary[$ruleSummary.triggered] &&
        SharedAuditsUtility.hasWcag(ruleInfo[$auditRule.standards]) &&
        severity === $severity.info
      ) {
        total += this.getMetricFromRuleSummary(ruleSummary, $ruleSummary.errors, componentId);
      }
    }

    return total;
  }
}
